<template>
  <div
    class="menu-item"
    :class="{
      'is-active': isActive,
      disabled: !isEnabled,
      hover: isHovered,
      ...itemClass,
    }"
    tabindex="0"
    @mouseover="isHovered = true"
    @mouseout="isHovered = false"
    @click="onClick"
  >
    <svg-icon
      v-if="item.icon"
      class="menu-icon"
      :width="'14'"
      :height="'14'"
      :icon="item.icon"
    />
    <div class="title" :class="itemClass">
      <label v-if="item.showName" :for="item.id">
        <span class="text">
          {{ item.name }}
        </span>
        <div v-if="item.upgrade" class="upgrade">Pro</div>
        <svg-icon
          v-if="submenu"
          :width="'8'"
          :height="'8'"
          icon="app-icons/arrow-right"
        />
      </label>
      <component
        :is="addon.component"
        v-if="addon"
        v-bind="addon.props"
        :id="item.id"
      />
    </div>
    <div v-if="submenu" ref="contextSubmenu" class="submenu" :style="styles">
      <component :is="submenu.component" v-bind="submenu.props" />
    </div>
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';
import SvgIcon from '../all/SvgIcon.vue';
import ColorPicker from '../all/ColorPicker/ColorPicker.vue';
import ConnectionStylePicker from '../all/ConnectionStylePicker.vue';

export default {
  components: {
    ColorPicker,
    ConnectionStylePicker,
    SvgIcon,
  },

  props: {
    id: {
      type: String,
      default: null,
    },
  },

  data() {
    return {
      isHovered: false,
      styles: {
        top: '0px',
        left: '100%',
        bottom: 'auto',
      },
    };
  },

  computed: {
    ...mapGetters('application/contextMenu', [
      'getItem',
      'getIsActive',
      'getIsEnabled',
      'getSubmenu',
      'getAddon',
      'getPosition',
    ]),

    item() {
      return this.getItem(this.id);
    },

    itemClass() {
      if (this.addon) {
        return {
          addonitem: true,
          [this.addon.component]: true,
        };
      }

      return {};
    },

    isActive() {
      return this.getIsActive(this.id);
    },

    isEnabled() {
      return this.getIsEnabled(this.id);
    },

    submenu() {
      return this.getSubmenu(this.id);
    },

    addon() {
      return this.getAddon(this.id);
    },
  },

  mounted() {
    if (this.submenu) {
      const { left, top } = this.getPosition;
      this.repositionSubMenu(top);
    }
  },

  methods: {
    ...mapActions('application/contextMenu', ['openSubmenu', 'doAction']),

    /**
     * Event handler for click on this menu item
     */
    onClick() {
      console.log('OnClick for menu item:', this.item);
      // Do nothing if the button is disabled or has submenu
      if (!this.isEnabled || this.item.submenu) {
        return;
      }
      if (this.addon && this.addon.showOnClick) {
        console.log('Show on click activated for this item:', this.item);
      }
      // Execute click command (if one exists)
      this.doAction({ id: this.id });
    },

    repositionSubMenu(top) {
      // Get Submenu Top ( depends on contextMenu )
      const submenuTop = this.$refs.contextSubmenu.getBoundingClientRect().top;

      // Get Submenu height
      const submenuHeight = this.$refs.contextSubmenu.offsetHeight;

      // Sum top and height
      const fixedHeight = submenuTop + submenuHeight;
      const largestHeight = window.innerHeight - fixedHeight - 15;

      let styles = { top: `0`, bottom: `auto` };
      if (top > largestHeight) {
        top = largestHeight - top;
        styles = { top: `${top}px`, bottom: `auto` };
        if (Math.abs(top - 30) >= submenuHeight) {
          top = -submenuHeight;
          styles = { top: `auto`, bottom: `0` };
        }
      } else {
        top = 0;
        styles = { top: `0`, bottom: `auto` };
      }

      this.styles = styles;
    },
  },
};
</script>

<style lang="scss" scoped>
.menu-item {
  display: flex;
  user-select: none;
  background-color: #252929;
  position: relative;
  border-bottom: 1px solid #313838;

  .menu-icon {
    margin-left: 7px;
    cursor: pointer;
  }

  &:last-child {
    border-bottom: none;
  }

  &:hover:not(.disabled) {
    &:not(.color-picker):not(.connection-style-picker) {
      background-color: #374144;
    }
    & > .title {
      color: #fff;

      &.color-picker {
        color: #9ba1a3;
        background-color: inherit;
      }

      &.connection-style-picker {
        color: #9ba1a3;
        background-color: inherit;
      }
    }
  }

  &.disabled .title,
  &.disabled .menu-icon {
    color: #414e4e;
    cursor: default;
  }

  .title {
    color: #9ba1a3;
    line-height: 34px;
    cursor: pointer;
    white-space: nowrap;
    min-width: 195px;
    display: flex;
    justify-content: space-between;
    align-items: center;
    label {
      cursor: inherit;
      width: 100%;
      display: flex;
      justify-content: space-between;
      align-items: center;
    }
    .upgrade {
      background: #db841e;
      border-radius: 4px;
      color: #fff;
      text-transform: uppercase;
      margin: 0 8px 0 0;
      padding: 4px 6px 4px 6px;
      line-height: 1;
      font-size: 11px;
      font-weight: 600;
    }

    &.color-picker {
      display: flex;
      flex-flow: column;
      align-items: flex-start;
      cursor: default;
    }

    span {
      flex: 1;
      padding: 0 12px 0 12px;
    }
  }

  &.colors {
    display: flex;
    flex-flow: column;

    &:hover > .title {
      color: #9ba1a3;
      background-color: transparent;
      cursor: default;
    }
    ul {
      padding: 0 12px 6px;
      margin: 0;
      display: flex;
      li {
        list-style: none;
        button {
          margin: 0 8px 0 0;
          outline: 0;
          text-decoration: none;
          cursor: pointer;
          width: 20px;
          height: 20px;
          background: none;
          border: none;
          position: relative;

          &:after {
            content: '';
            width: 20px;
            height: 20px;
            border-radius: 999px;
            position: absolute;
            // background: red;
            top: 0;
            left: 0;
          }

          &.default:after {
            background: #2c3233;
            border: 1px solid lighten(#2c3233, 5%);
          }

          &.default:hover:after {
            background: lighten(#2c3233, 5%);
            border: 1px solid lighten(#2c3233, 10%);
          }

          &.red:after {
            background: #4e1b0c;
            border: 1px solid lighten(#4e1b0c, 5%);
          }

          &.red:hover:after {
            background: lighten(#4e1b0c, 5%);
            border: 1px solid lighten(#4e1b0c, 10%);
          }

          &.blue:after {
            background: #09344e;
            border: 1px solid lighten(#09344e, 5%);
          }

          &.blue:hover:after {
            background: lighten(#09344e, 5%);
            border: 1px solid lighten(#09344e, 10%);
          }

          &.purple:after {
            background: #4a0c45;
            border: 1px solid lighten(#4a0c45, 5%);
          }

          &.purple:hover:after {
            background: lighten(#4a0c45, 5%);
            border: 1px solid lighten(#4a0c45, 10%);
          }

          &.cyan:after {
            background: #13474c;
            border: 1px solid lighten(#13474c, 5%);
          }

          &.cyan:hover:after {
            background: lighten(#13474c, 5%);
            border: 1px solid lighten(#13474c, 10%);
          }

          &.green:after {
            background: #0b3a11;
            border: 1px solid lighten(#0b3a11, 5%);
          }

          &.green:hover:after {
            background: lighten(#0b3a11, 5%);
            border: 1px solid lighten(#0b3a11, 10%);
          }
        }
      }
    }
  }
}

.menu-item > .title > .svg-icon-container {
  margin-right: 6px;
}

.menu-item > .submenu {
  position: absolute;
  left: 100%;
  top: 0;
  opacity: 0;
  visibility: hidden;
  transition:
    opacity 100ms 0.5s,
    visibility 100ms 0.5s;
  background: #252929;
  // border-left: 1px solid #313838;
  padding: 8px;
  /* border: 1px solid #313838; */
  box-shadow: 0 4px 10px 0 rgba(0, 0, 0, 0.2);
}

.menu-item.hover > .submenu {
  opacity: 1;
  visibility: visible;
  transition:
    opacity 100ms,
    visibility 100ms;
}

.menu-item.picker {
  display: flex;
  width: 150px;
  height: 100%;
  flex-wrap: wrap;
  justify-content: center;
  align-items: center;
}

.menu-item.picker .circle {
  width: 30px;
  height: 30px;
  // color: #fff;
  padding: 25px;
  display: flex;
  justify-content: center;
  align-items: center;
  cursor: pointer;

  &:hover {
    background-color: #374144;
  }

  &:after {
    content: '';
    width: 20px;
    height: 20px;
    border-radius: 999px;
    position: absolute;
  }
}

.menu-item.picker .circle.default {
  &:after {
    background: #0d0e0e;
  }
}

.menu-item.picker .circle.red {
  &:after {
    background: #4e1b0c;
  }
}

.menu-item.picker .circle.blue {
  &:after {
    background: #0c2c4e;
  }
}

.menu-item.picker .circle.purple {
  &:after {
    background: #3e0e4a;
  }
}

.menu-item.picker .circle.cyan {
  &:after {
    background: #0a3848;
  }
}

.menu-item.picker .circle.green {
  &:after {
    background: #1b3c07;
  }
}
</style>
